import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { useSettings } from "@/context/SettingsContext";
import { ShieldCheck, Gem, Tag, Lock, Sparkles, ArrowRight, Truck } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const whyChoose = [
  { icon: Gem, title: "Genuine 9Karat Gold", desc: "Every gold chain is crafted from certified 9K gold with honest purity." },
  { icon: Sparkles, title: "Quality Craftsmanship", desc: "Precise links and flawless finishing from skilled artisans." },
  { icon: Tag, title: "Transparent Pricing", desc: "Clear MRP and discounted price on every product. No hidden charges." },
  { icon: Lock, title: "Secure Ordering", desc: "Your data and orders are protected end to end." },
  { icon: ShieldCheck, title: "Trusted Service", desc: "Reliable support before and after every purchase." },
  { icon: Truck, title: "Fast Delivery", desc: "Carefully packed and shipped across India within days." },
];

export default function Home() {
  const { settings } = useSettings();
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 8))).catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative bg-obsidian text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1613966561243-c6959a886009?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Gold chain"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-36">
          <div className="max-w-xl animate-fade-up">
            <p className="overline text-gold mb-5">{settings.tagline || "GP · Gold · Silver Chains"}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
              Timeless 9Karat Gold Chains, Honestly Priced
            </h1>
            <p className="text-white/70 text-base lg:text-lg leading-relaxed mb-9 max-w-md">
              Discover a curated collection of gold, silver and gold-plated chains crafted with care. Premium quality, transparent pricing, delivered across India.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/catalogue"
                className="inline-flex items-center gap-2 bg-gold text-obsidian font-semibold px-7 py-3.5 hover:bg-gold-light transition-colors rounded-sm"
                data-testid="hero-shop-btn"
              >
                Shop Gold Chains <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/catalogue"
                className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold px-7 py-3.5 hover:bg-white hover:text-obsidian transition-colors rounded-sm"
                data-testid="hero-explore-btn"
              >
                Explore Collection
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline text-gold-dark mb-2">Featured Collection</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-obsidian">Popular Chains</h2>
          </div>
          <Link to="/catalogue" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold hover:text-gold-dark transition-colors" data-testid="view-all-btn">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {featured === null ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {["s1", "s2", "s3", "s4"].map((k) => (
              <div key={k}>
                <Skeleton className="aspect-[4/5] w-full" />
                <Skeleton className="h-4 w-2/3 mt-4" />
                <Skeleton className="h-6 w-1/2 mt-2" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* Why Choose Us */}
      <section className="bg-white border-y border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="overline text-gold-dark mb-2">Why ChainSmart</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-obsidian">Reasons to Shop With Us</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10">
            {whyChoose.map((w) => (
              <div key={w.title} className="bg-white p-8 group" data-testid={`why-${w.title.split(" ")[1]?.toLowerCase()}`}>
                <div className="w-12 h-12 flex items-center justify-center bg-obsidian text-gold mb-5 group-hover:bg-gold group-hover:text-obsidian transition-colors rounded-sm">
                  <w.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-2xl text-obsidian mb-2">{w.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About teaser */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1585960622850-ed33c41d6418?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="ChainSmart"
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
          <div>
            <p className="overline text-gold-dark mb-3">About ChainSmart</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-obsidian mb-5 leading-tight">
              Crafting Trust, One Chain at a Time
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              {settings.about}
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 bg-obsidian text-white font-semibold px-7 py-3.5 hover:bg-gold hover:text-obsidian transition-colors rounded-sm"
              data-testid="about-cta"
            >
              Learn More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
